import { type Component, type JSX, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { isInFloatingLayer, registerOpenDialog } from './floatingLayer';

interface DialogShellProps {
  onClose: () => void;
  maxWidth?: string;
  zIndex?: number;
  align?: 'center' | 'bottom';
  children: JSX.Element;
}

const DialogShellInner: Component<DialogShellProps> = (props) => {
  let dialog: HTMLDialogElement | undefined;
  let box: HTMLDivElement | undefined;

  let unregister: (() => void) | undefined;
  onMount(() => {
    dialog?.showModal?.();
    if (dialog) unregister = registerOpenDialog(dialog);
  });

  onCleanup(() => {
    unregister?.();
    dialog?.close?.();
    document.body.style.overflow = '';
  });

  const onCancel = (e: Event) => {
    e.preventDefault();
    props.onClose();
  };

  const onClick = (e: MouseEvent) => {
    if (isInFloatingLayer(e.target)) return;
    if (box && e.target instanceof Node && !box.contains(e.target)) props.onClose();
  };

  const alignClass = () => (props.align === 'bottom' ? 'items-end sm:items-center' : 'items-center');

  return (
    <dialog
      ref={dialog}
      class={`fixed inset-0 m-0 h-full max-h-full w-full max-w-full bg-transparent p-0`}
      onCancel={onCancel}
      onClick={onClick}
    >
      <div class={`flex h-full w-full justify-center px-4 py-6 ${alignClass()}`}>
        <div
          ref={box}
          class="glass w-full max-h-full overflow-y-auto"
          style={{
            'max-width': props.maxWidth ?? '560px',
            'border-radius': '16px',
            'box-shadow':
              '0 30px 80px -10px rgba(0,0,0,0.55), inset 0 0 0 1px var(--color-border-hairline)',
          }}
        >
          {props.children}
        </div>
      </div>
    </dialog>
  );
};

export const DialogShell: Component<DialogShellProps> = (props) => (
  <Portal>
    <DialogShellInner {...props} />
  </Portal>
);
